import { paramId } from 'features/dataLayers/cdfParamValue'
import {
  isWorkflowGeneratedCdf,
  withReusableWorkflowGeneratedCdfMetadata,
} from 'features/dataLayers/generatedCdfVisibility'
import { GeckoComponentDataFrame, GeckoView, RolodexEntryJson } from 'types'

export const CanonicalShortTermConditionLayerKind = {
  ShortTermCorrosionRate: 'short-term-corrosion-rate',
  ShortTermRemainingLife: 'short-term-remaining-life',
} as const

export type CanonicalShortTermConditionLayerKind =
  (typeof CanonicalShortTermConditionLayerKind)[keyof typeof CanonicalShortTermConditionLayerKind]

const CANONICAL_LAYER_PARAM = 'forecast_canonical_short_term_condition_layer'
const CANONICAL_LAYER_KIND_PARAM =
  'forecast_canonical_short_term_condition_layer_kind'

const CANONICAL_ID_PREFIX_BY_KIND: Record<
  CanonicalShortTermConditionLayerKind,
  string
> = {
  [CanonicalShortTermConditionLayerKind.ShortTermCorrosionRate]:
    'asset-lifecycle-short-term-corrosion-rate',
  [CanonicalShortTermConditionLayerKind.ShortTermRemainingLife]:
    'asset-lifecycle-short-term-remaining-life',
}

const VIEW_ID_BY_KIND: Record<CanonicalShortTermConditionLayerKind, string> = {
  [CanonicalShortTermConditionLayerKind.ShortTermCorrosionRate]:
    GeckoView.ShortTermCorrosionRate.id,
  [CanonicalShortTermConditionLayerKind.ShortTermRemainingLife]:
    GeckoView.ShortTermRemainingLife.id,
}

export function getCanonicalShortTermConditionLayerId(
  kind: CanonicalShortTermConditionLayerKind,
  componentId: string,
  inspectionId: string
): string {
  return `${CANONICAL_ID_PREFIX_BY_KIND[kind]}-${componentId}-${inspectionId}`
}

export function withCanonicalShortTermConditionLayerMetadata(
  cdf: GeckoComponentDataFrame,
  kind: CanonicalShortTermConditionLayerKind
): GeckoComponentDataFrame {
  const componentId = cdf.tags?.component
  const inspectionId = cdf.tags?.inspection
  if (!componentId || !inspectionId) {
    throw new Error(
      `Cannot canonicalize ${kind} layer without component and inspection tags.`
    )
  }

  const actualViewId = paramId(cdf.data.params.view)
  const expectedViewId = VIEW_ID_BY_KIND[kind]
  if (actualViewId !== expectedViewId) {
    throw new Error(
      `Cannot canonicalize CDF with view "${actualViewId}" as "${kind}" (expected "${expectedViewId}").`
    )
  }

  const reusableCdf = withReusableWorkflowGeneratedCdfMetadata(cdf, {})
  const params = {
    ...(reusableCdf.raw.data.params ?? {}),
    [CANONICAL_LAYER_PARAM]: true,
    [CANONICAL_LAYER_KIND_PARAM]: kind,
  }
  const tags = {
    ...(reusableCdf.raw.tags ?? {}),
    ...(reusableCdf.tags ?? {}),
    component: componentId,
    inspection: inspectionId,
  }

  return GeckoComponentDataFrame.CreateFromJSON({
    ...reusableCdf.raw,
    id: getCanonicalShortTermConditionLayerId(kind, componentId, inspectionId),
    tags,
    data: {
      ...reusableCdf.raw.data,
      params,
    },
  })
}

export function isCanonicalShortTermConditionLayer(
  cdf: GeckoComponentDataFrame,
  kind?: CanonicalShortTermConditionLayerKind
): boolean {
  const canonicalKind = getCanonicalShortTermConditionLayerKind(cdf)
  if (!canonicalKind || (kind && canonicalKind !== kind)) return false

  const componentId = cdf.tags?.component
  const inspectionId = cdf.tags?.inspection
  if (!componentId || !inspectionId) return false

  return paramId(cdf.data.params.view) === VIEW_ID_BY_KIND[canonicalKind]
}

/**
 * Raw entry-json variant of `isCanonicalShortTermConditionLayer`, for callers
 * (like the Rolodex entry dispenser) that decide on entries before any class
 * object exists.
 */
export function isCanonicalShortTermConditionLayerEntryJson(
  entry: RolodexEntryJson
): boolean {
  const params = (entry.data as { params?: Record<string, unknown> })?.params
  return params?.[CANONICAL_LAYER_PARAM] === true
}

export function filterSupersededShortTermConditionLayers(
  cdfs: readonly GeckoComponentDataFrame[]
): GeckoComponentDataFrame[] {
  const canonicalKeys = new Set<string>()
  for (const cdf of cdfs) {
    const kind = getCanonicalShortTermConditionLayerKind(cdf)
    const componentId = cdf.tags?.component
    const inspectionId = cdf.tags?.inspection
    if (
      !kind ||
      !componentId ||
      !inspectionId ||
      !isCanonicalShortTermConditionLayer(cdf, kind)
    ) {
      continue
    }
    canonicalKeys.add(getLayerScopeKey(kind, componentId, inspectionId))
  }

  const filtered = cdfs.filter((cdf) => {
    if (isCanonicalShortTermConditionLayer(cdf)) return true
    if (!isWorkflowGeneratedCdf(cdf)) return true

    const kind = getShortTermConditionLayerKind(cdf)
    const componentId = cdf.tags?.component
    const inspectionId = cdf.tags?.inspection
    if (!kind || !componentId || !inspectionId) return true

    return !canonicalKeys.has(getLayerScopeKey(kind, componentId, inspectionId))
  })

  return uniqueCdfsByIdPreferNewest(
    uniqueCanonicalCdfsByScopePreferNewest(filtered)
  )
}

/**
 * A session-generated canonical layer and its persisted (dispensed) copy
 * carry different ids, so collapse canonical layers per (kind, component,
 * inspection) keeping the newest.
 */
function uniqueCanonicalCdfsByScopePreferNewest(
  cdfs: readonly GeckoComponentDataFrame[]
): GeckoComponentDataFrame[] {
  const newestByScope = new Map<string, GeckoComponentDataFrame>()
  for (const cdf of cdfs) {
    const scopeKey = getCanonicalLayerScopeKey(cdf)
    if (!scopeKey) continue
    const existing = newestByScope.get(scopeKey)
    if (!existing || cdf.generation > existing.generation) {
      newestByScope.set(scopeKey, cdf)
    }
  }

  return cdfs.filter((cdf) => {
    const scopeKey = getCanonicalLayerScopeKey(cdf)
    return !scopeKey || newestByScope.get(scopeKey) === cdf
  })
}

function getCanonicalLayerScopeKey(
  cdf: GeckoComponentDataFrame
): string | undefined {
  if (!isCanonicalShortTermConditionLayer(cdf)) return undefined
  const kind = getCanonicalShortTermConditionLayerKind(cdf)
  const componentId = cdf.tags?.component
  const inspectionId = cdf.tags?.inspection
  if (!kind || !componentId || !inspectionId) return undefined
  return getLayerScopeKey(kind, componentId, inspectionId)
}

function getCanonicalShortTermConditionLayerKind(
  cdf: GeckoComponentDataFrame
): CanonicalShortTermConditionLayerKind | undefined {
  const params = getParams(cdf)
  if (params[CANONICAL_LAYER_PARAM] !== true) return undefined

  const kind = params[CANONICAL_LAYER_KIND_PARAM]
  return isCanonicalShortTermConditionLayerKind(kind) ? kind : undefined
}

function getShortTermConditionLayerKind(
  cdf: GeckoComponentDataFrame
): CanonicalShortTermConditionLayerKind | undefined {
  const canonicalKind = getCanonicalShortTermConditionLayerKind(cdf)
  if (canonicalKind) return canonicalKind

  const viewId = paramId(cdf.data.params.view)
  if (viewId === GeckoView.ShortTermCorrosionRate.id) {
    return CanonicalShortTermConditionLayerKind.ShortTermCorrosionRate
  }

  if (viewId === GeckoView.ShortTermRemainingLife.id) {
    return CanonicalShortTermConditionLayerKind.ShortTermRemainingLife
  }

  return undefined
}

function isCanonicalShortTermConditionLayerKind(
  value: unknown
): value is CanonicalShortTermConditionLayerKind {
  return (
    Object.values(CanonicalShortTermConditionLayerKind) as unknown[]
  ).includes(value)
}

function getLayerScopeKey(
  kind: CanonicalShortTermConditionLayerKind,
  componentId: string,
  inspectionId: string
): string {
  return `${kind}\u001f${componentId}\u001f${inspectionId}`
}

function uniqueCdfsByIdPreferNewest(
  cdfs: readonly GeckoComponentDataFrame[]
): GeckoComponentDataFrame[] {
  const cdfsById = new Map<string, GeckoComponentDataFrame>()
  for (const cdf of cdfs) {
    const existing = cdfsById.get(cdf.id)
    if (!existing || cdf.generation > existing.generation) {
      cdfsById.set(cdf.id, cdf)
    }
  }
  return Array.from(cdfsById.values())
}

function getParams(cdf: GeckoComponentDataFrame): Record<string, unknown> {
  return cdf.data.params as unknown as Record<string, unknown>
}
